/**
 * /shop/ category rail overflow handler + active-tab sync.
 *
 * The rail is a single horizontal row with category tabs on the left,
 * a flex-1 spacer, and a "DOWNLOAD FULL LINE SHEET" link on the right.
 * Two responsibilities:
 *
 *   1. Overflow handling. When the tabs don't all fit, hide rightmost
 *      tabs and inject a "+ MORE" button that toggles `.is-expanded`
 *      (flex-wrap) on click. Re-measures on resize / ResizeObserver.
 *
 *   2. Active-tab sync. The SSR pass paints `.is-active` on the tab
 *      whose href matches the current URL. AJAX nav (filter rail
 *      uncheck → /shop/, click another category, popstate) doesn't
 *      re-render the rail, so we listen on shop:state-restored and
 *      re-derive the active tab from location.pathname.
 */

const SELECTOR_RAIL   = '.tx-shop__category-rail';
const SELECTOR_TAB    = '.tx-shop__cat-tab';
const ACTIVE_CLASS    = 'is-active';

const MORE_LABEL = '+ MORE';
const LESS_LABEL = '− LESS';

const HIDDEN_CLASS = 'is-hidden';

/**
 * Re-derive which tab is .is-active from the current URL's pathname.
 * Each tab is an <a href=…>; we compare its pathname to location's.
 * Trailing-slash normalization keeps /shop and /shop/ equivalent.
 */
const normalizePath = (p) => (p && p !== '/' && p.endsWith('/') ? p.slice(0, -1) : p) || '/';

const syncActiveTab = (rail, tabs) => {
    const current = normalizePath(location.pathname);
    tabs.forEach((tab) => {
        if (!(tab instanceof HTMLAnchorElement)) return;
        const tabPath = normalizePath(new URL(tab.href, location.origin).pathname);
        const match   = tabPath === current;
        tab.classList.toggle(ACTIVE_CLASS, match);
        if (match) {
            tab.setAttribute('aria-current', 'page');
        } else {
            tab.removeAttribute('aria-current');
        }
    });
};

const init = () => {
    const rail = document.querySelector(SELECTOR_RAIL);
    if (!rail) return;

    const tabs       = Array.from(rail.querySelectorAll(SELECTOR_TAB));
    const downloadEl = rail.querySelector('.tx-shop__cat-rail-download');
    const spacerEl   = rail.querySelector('.tx-shop__cat-rail-spacer');
    if (!tabs.length) return;

    // First sync from current URL (handles cases where SSR painted
    // is-active from a different signal than path — and is cheap
    // enough to run unconditionally).
    syncActiveTab(rail, tabs);
    document.addEventListener('shop:state-restored', () => syncActiveTab(rail, tabs));

    // Inject the toggle button right before the spacer.
    const moreBtn = document.createElement('button');
    moreBtn.type      = 'button';
    moreBtn.className = `mono tx-shop__cat-rail-more ${HIDDEN_CLASS}`;
    moreBtn.textContent = MORE_LABEL;
    moreBtn.setAttribute('aria-expanded', 'false');
    rail.insertBefore(moreBtn, spacerEl || downloadEl || null);

    let isExpanded = false;

    const showAllTabs = () => tabs.forEach((t) => t.classList.remove(HIDDEN_CLASS));

    const collapseLayout = () => {
        showAllTabs();

        // Reveal MORE button first so its width counts in the measurement.
        moreBtn.classList.remove(HIDDEN_CLASS);
        moreBtn.textContent = MORE_LABEL;
        moreBtn.setAttribute('aria-expanded', 'false');

        // If nothing overflows, hide MORE again and bail.
        if (rail.scrollWidth <= rail.clientWidth + 1) {
            moreBtn.classList.add(HIDDEN_CLASS);
            return;
        }

        // Hide tabs from the right (but never the first / ALL tab) until
        // the row fits. Reading scrollWidth inside the loop forces a
        // reflow per iteration, so this is O(n) layout passes — fine
        // for the small N here.
        for (let i = tabs.length - 1; i > 0; i--) {
            if (rail.scrollWidth <= rail.clientWidth + 1) break;
            tabs[i].classList.add(HIDDEN_CLASS);
        }
    };

    const layout = () => {
        if (isExpanded) return;
        collapseLayout();
    };

    const setExpanded = (expanded) => {
        isExpanded = expanded;
        rail.classList.toggle('is-expanded', expanded);
        moreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');

        if (expanded) {
            showAllTabs();
            moreBtn.textContent = LESS_LABEL;
            moreBtn.classList.remove(HIDDEN_CLASS);
        } else {
            moreBtn.textContent = MORE_LABEL;
            collapseLayout();
        }
    };

    moreBtn.addEventListener('click', () => setExpanded(!isExpanded));

    // Layout once on first paint, and again after the load event so
    // late-loading fonts that change tab widths get accounted for.
    requestAnimationFrame(layout);
    window.addEventListener('load', layout);

    let resizeRaf = 0;
    const onResize = () => {
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(layout);
    };
    window.addEventListener('resize', onResize);

    if (typeof ResizeObserver === 'function') {
        new ResizeObserver(onResize).observe(rail);
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
